// @ts-check
import { API_BASE, resolveAdminMediaUrl } from './config.js';
import { readAdminSession } from './admin-session.js';
import {
  PUZZLE_ICON_SVG,
  GAMEPAD_ICON_SVG,
  USERS_ICON_SVG,
  SEARCH_ICON_SVG,
  COPY_ICON_SVG,
  CHECK_ICON_SVG,
  ALERT_ICON_SVG,
  CRITICAL_ICON_SVG,
  BOLT_ICON_SVG,
  CLOCK_ICON_SVG,
  FLAME_ICON_SVG,
  GLOBE_ICON_SVG,
  getMilestoneSvgIcon,
  CLOSE_ICON_SVG
} from '../../shared/icons.js';


export const DASH_POLL_STORAGE_KEY = 'puzzle_admin_dash_poll_interval';

/** @type {ReturnType<typeof setInterval>|null} */
let dashboardPollTimer = null;
/** @type {ReturnType<typeof setInterval>|null} */
let stalenessTimer = null;
let lastDashboardRefreshTime = Date.now();
let dashboardPollInterval = 30; // default 30 seconds
let isRefreshing = false;

/**
 * Loads the persisted dashboard polling interval from storage.
 *
 * @param {Pick<Storage, 'getItem'>|null} [storage]
 * @returns {number}
 */
export function loadStoredDashboardPollInterval(storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  try {
    const saved = storage ? storage.getItem(DASH_POLL_STORAGE_KEY) : null;
    if (saved !== null) {
      const parsed = Number(saved);
      if (Number.isFinite(parsed) && parsed >= 0) {
        dashboardPollInterval = parsed;
      }
    }
  } catch {}
  return dashboardPollInterval;
}

loadStoredDashboardPollInterval();

/**
 * Helper to get the auth token from session
 * @returns {string|null}
 */
function getAuthToken() {
  const session = readAdminSession(window.localStorage);
  return session ? session.token : null;
}

/**
 * Gets currently active auto-refresh polling interval in seconds
 * @returns {number}
 */
export function getDashboardPollInterval() {
  return dashboardPollInterval;
}

/**
 * Sets dashboard polling interval in seconds and updates localStorage
 * @param {number} seconds - 0 to disable, or positive seconds
 */
export function setDashboardPollInterval(seconds) {
  dashboardPollInterval = Math.max(0, Number(seconds) || 0);
  try {
    localStorage.setItem(DASH_POLL_STORAGE_KEY, String(dashboardPollInterval));
  } catch {}
  syncDashboardPolling(true);
}

/**
 * Updates the visual staleness text (e.g. "Updated just now", "Updated 15s ago")
 */
export function updateDashboardStalenessIndicator() {
  const el = document.getElementById('dash-last-updated');
  if (!el) return;
  const elapsedSec = Math.floor((Date.now() - lastDashboardRefreshTime) / 1000);
  if (elapsedSec < 5) {
    el.textContent = 'Updated just now';
  } else if (elapsedSec < 60) {
    el.textContent = `Updated ${elapsedSec}s ago`;
  } else {
    const min = Math.floor(elapsedSec / 60);
    el.textContent = `Updated ${min}m ago`;
  }
}

/**
 * Starts/stops auto-refresh interval when the dashboard tab is active.
 * Respects user's configured dashboardPollInterval (or 0 for manual).
 * @param {string|boolean} isActive - whether dashboard tab is active
 */
export function syncDashboardPolling(isActive) {
  const shouldPoll = isActive === 'dashboard' || isActive === true;

  if (dashboardPollTimer) {
    clearInterval(dashboardPollTimer);
    dashboardPollTimer = null;
  }
  if (stalenessTimer) {
    clearInterval(stalenessTimer);
    stalenessTimer = null;
  }

  if (shouldPoll) {
    updateDashboardStalenessIndicator();
    stalenessTimer = setInterval(updateDashboardStalenessIndicator, 5000);

    if (dashboardPollInterval > 0) {
      dashboardPollTimer = setInterval(() => {
        if (document.visibilityState === 'visible') {
          refreshDashboard();
        }
      }, dashboardPollInterval * 1000);
    }
  }
}

/**
 * Fetches GET ${API_BASE}/api/admin/dashboard with auth header,
 * then populates all DOM elements and updates refresh timestamps
 */
export async function refreshDashboard() {
  if (isRefreshing) return;
  isRefreshing = true;

  const refreshBtn = document.getElementById('dash-refresh-now-btn');
  const refreshIcon = document.getElementById('dash-refresh-icon');
  if (refreshIcon) refreshIcon.classList.add('spinning');
  if (refreshBtn) refreshBtn.setAttribute('disabled', 'true');

  try {
    const token = getAuthToken();
    if (!token) return;

    const res = await fetch(`${API_BASE}/api/admin/dashboard`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (res.status === 401 || res.status === 403) {
      window.dispatchEvent(new CustomEvent('session-expired'));
      return;
    }

    if (!res.ok) {
      console.error('Dashboard fetch failed with status:', res.status);
      return;
    }

    const data = await res.json();
    lastDashboardRefreshTime = Date.now();
    updateDashboardStalenessIndicator();
    populateDashboard(data);
  } catch (err) {
    console.error('Dashboard fetch error:', err);
  } finally {
    isRefreshing = false;
    if (refreshIcon) refreshIcon.classList.remove('spinning');
    if (refreshBtn) refreshBtn.removeAttribute('disabled');
  }
}

/**
 * Wires the auto-refresh dropdown, manual refresh button, and document visibility listener
 */
export function initDashboardControls() {
  const select = /** @type {HTMLSelectElement|null} */ (document.getElementById('dash-poll-interval'));
  if (select) {
    select.value = String(dashboardPollInterval);
    select.addEventListener('change', () => {
      setDashboardPollInterval(Number(select.value));
    });
  }

  const refreshBtn = document.getElementById('dash-refresh-now-btn');
  refreshBtn?.addEventListener('click', () => {
    refreshDashboard();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const elapsed = Date.now() - lastDashboardRefreshTime;
      if (dashboardPollInterval > 0 && elapsed >= dashboardPollInterval * 1000) {
        refreshDashboard();
        syncDashboardPolling(true);
      } else {
        updateDashboardStalenessIndicator();
      }
    }
  });
}

/**
 * @param {string} id
 * @param {any} val
 */
function setEl(id, val) {
  const el = document.getElementById(id);
  if (el) {
    el.textContent = (val !== null && val !== undefined) ? String(val) : '—';
  }
}

/**
 * @param {string} id
 * @param {string} html
 */
function setElHtml(id, html) {
  const el = document.getElementById(id);
  if (el) {
    el.innerHTML = html;
  }
}

/**
 * @param {string} id
 * @param {string} countId
 * @param {number|undefined} count
 */
function toggleOp(id, countId, count) {
  const el = document.getElementById(id);
  const countEl = document.getElementById(countId);
  const num = Number(count) || 0;
  if (el) {
    if (num > 0) {
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', '');
    }
  }
  if (countEl) countEl.textContent = String(num);
}

/**
 * Renders a lightweight, smooth inline SVG sparkline graph
 * @param {string} containerId
 * @param {Array<number|{value?: number, count?: number, label?: string}>} points
 * @param {{
 *   color?: string,
 *   fill?: boolean,
 *   width?: number,
 *   height?: number,
 *   gradientId?: string
 * }} [options]
 */
export function renderSparkline(containerId, points, options = {}) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';

  if (!points || points.length === 0) {
    container.innerHTML = '<span class="dash-sparkline-flat" aria-hidden="true"></span>';
    return;
  }

  const vals = points.map(p => {
    if (typeof p === 'number') return Number.isFinite(p) ? p : 0;
    return Number(p?.value ?? p?.count ?? 0) || 0;
  });

  const w = options.width || 120;
  const h = options.height || 32;
  const pad = 3;
  const effW = w - pad * 2;
  const effH = h - pad * 2;

  const minVal = Math.min(...vals);
  const maxVal = Math.max(...vals);
  const range = maxVal - minVal;

  const color = options.color || '#38bdf8';
  const fill = options.fill !== false;
  const gradId = options.gradientId || `spark-grad-${containerId}-${Math.floor(Math.random() * 10000)}`;

  const coords = vals.map((val, idx) => {
    const x = pad + (idx / Math.max(1, vals.length - 1)) * effW;
    const y = range === 0 ? pad + effH / 2 : pad + effH - ((val - minVal) / range) * effH;
    return { x: Number(x.toFixed(1)), y: Number(y.toFixed(1)) };
  });

  const pathD = coords.reduce((acc, pt, i) => `${acc} ${i === 0 ? 'M' : 'L'} ${pt.x} ${pt.y}`, '');
  const lastX = coords[coords.length - 1].x;
  const areaD = `${pathD} L ${lastX} ${h} L ${coords[0].x} ${h} Z`;

  const firstVal = vals[0];
  const lastVal = vals[vals.length - 1];
  const trendDiff = lastVal - firstVal;
  const trendIcon = trendDiff > 0 ? '↗' : trendDiff < 0 ? '↘' : '→';
  const trendPct = firstVal > 0 ? `${trendDiff > 0 ? '+' : ''}${Math.round((trendDiff / firstVal) * 100)}%` : `${trendDiff >= 0 ? '+' : ''}${trendDiff}`;

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  svg.setAttribute('class', 'dash-sparkline-svg');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', `Sparkline trend: ${firstVal} to ${lastVal} (${trendPct})`);

  let defsHtml = '';
  if (fill) {
    defsHtml = `
      <defs>
        <linearGradient id="${gradId}" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="${color}" stop-opacity="0.32" />
          <stop offset="100%" stop-color="${color}" stop-opacity="0.02" />
        </linearGradient>
      </defs>
      <path d="${areaD}" fill="url(#${gradId})" />
    `;
  }

  svg.innerHTML = `
    ${defsHtml}
    <path d="${pathD}" fill="none" stroke="${color}" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
    <circle cx="${coords[coords.length - 1].x}" cy="${coords[coords.length - 1].y}" r="2" fill="${color}" />
  `;

  const wrap = document.createElement('div');
  wrap.className = 'dash-sparkline-wrap';
  wrap.appendChild(svg);

  const badge = document.createElement('span');
  badge.className = `dash-sparkline-badge ${trendDiff > 0 ? 'trend-up' : trendDiff < 0 ? 'trend-down' : 'trend-flat'}`;
  badge.textContent = `${trendIcon} ${trendPct}`;
  badge.title = `Trend from ${firstVal} to ${lastVal} across period`;
  wrap.appendChild(badge);

  container.appendChild(wrap);
}

/**
 * Populates the System & Infrastructure Health panel
 * @param {any} health
 * @param {any} data
 */
export function renderSystemHealth(health, data) {
  const panel = document.getElementById('dash-system-health-panel');
  if (!panel) return;

  const overallBadge = document.getElementById('dash-health-overall-badge');
  const isDegraded = health?.status === 'degraded';
  if (overallBadge) {
    overallBadge.className = isDegraded ? 'badge badge-health badge-health-warn' : 'badge badge-health badge-health-ok';
    overallBadge.innerHTML = isDegraded ? `${ALERT_ICON_SVG} Degraded` : '● Operational';
  }

  setEl('dash-health-colo', health?.edge?.colo || 'Local');
  setEl('dash-health-db-latency', health?.dbLatencyMs !== undefined ? `${health.dbLatencyMs} ms` : '—');
  setEl('dash-health-total-rows', health?.totalRows !== undefined ? Number(health.totalRows).toLocaleString() : '—');

  const doRooms = health?.durableObjects?.activeRooms ?? data?.rooms?.active ?? 0;
  setEl('dash-health-do-rooms', `${doRooms} active`);
  const doSessions = health?.durableObjects?.connectedSessions ?? data?.rooms?.onlinePlayers ?? 0;
  setEl('dash-health-do-sessions', doSessions);

  const r2Imgs = health?.catalogImages ?? ((data?.images?.published || 0) + (data?.images?.unpublished || 0));
  setEl('dash-health-r2-images', `${r2Imgs} assets`);
  setEl('dash-health-custom-uploads', health?.customUploads ?? 0);

  const openErrors = data?.operations?.openErrors ?? 0;
  const errorsEl = document.getElementById('dash-health-errors-status');
  if (errorsEl) {
    errorsEl.textContent = openErrors > 0 ? `${openErrors} Issues` : 'Nominal';
    errorsEl.className = openErrors > 0 ? 'dash-health-metric-val val-warn' : 'dash-health-metric-val val-ok';
  }
  setEl('dash-health-open-errors', openErrors);
}

/**
 * Creates an extra alert banner DOM node for secondary active alerts.
 * @param {any} alert
 * @param {string} dismissedKey
 * @param {() => void} onDismiss
 * @returns {HTMLElement}
 */
export function createAlertBannerElement(alert, dismissedKey, onDismiss) {
  const div = document.createElement('div');
  const severity = alert.severity || 'warning';
  div.className = `dash-alert-banner dash-alert-banner-extra alert-${severity}`;
  div.setAttribute('role', 'alert');
  div.style.display = 'flex';

  const iconSvg = severity === 'critical' ? CRITICAL_ICON_SVG : alert.type === 'latency' ? BOLT_ICON_SVG : ALERT_ICON_SVG;
  const sig = `${alert.type}:${alert.timestamp}:${alert.title}`;

  const content = document.createElement('div');
  content.className = 'dash-alert-content';

  const iconEl = document.createElement('span');
  iconEl.className = 'dash-alert-icon';
  iconEl.innerHTML = iconSvg;
  content.appendChild(iconEl);

  const text = document.createElement('div');
  text.className = 'dash-alert-text';

  const titleEl = document.createElement('span');
  titleEl.className = 'dash-alert-title';
  titleEl.textContent = alert.title || 'Incident Alert';
  text.appendChild(titleEl);

  const msgEl = document.createElement('span');
  msgEl.className = 'dash-alert-message';
  const rawMsg = String(alert.message || 'An anomaly has been detected.');
  msgEl.textContent = rawMsg.length > 200 ? rawMsg.slice(0, 197) + '...' : rawMsg;
  text.appendChild(msgEl);

  content.appendChild(text);
  div.appendChild(content);

  const actions = document.createElement('div');
  actions.className = 'dash-alert-actions';

  const actionBtn = document.createElement('button');
  actionBtn.type = 'button';
  actionBtn.className = 'btn btn-sm dash-alert-action-btn';
  actionBtn.textContent = `${alert.actionText || 'Inspect'} →`;
  actionBtn.onclick = (e) => {
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent('dashboard:alert-action', {
      detail: {
        target: alert.actionTarget,
        errorId: alert.errorId,
        type: alert.type
      }
    }));
  };
  actions.appendChild(actionBtn);

  const dismissBtn = document.createElement('button');
  dismissBtn.type = 'button';
  dismissBtn.className = 'dash-alert-dismiss-btn';
  dismissBtn.title = 'Dismiss alert';
  dismissBtn.setAttribute('aria-label', 'Dismiss alert');
  dismissBtn.innerHTML = CLOSE_ICON_SVG;
  dismissBtn.onclick = (e) => {
    e.stopPropagation();
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(`${dismissedKey}_${sig}`, '1');
      }
    } catch {}
    if (typeof div.remove === 'function') div.remove();
    if (typeof onDismiss === 'function') onDismiss();
  };
  actions.appendChild(dismissBtn);

  div.appendChild(actions);

  return div;
}

/**
 * Renders the incident & anomaly alert banner(s) (Recommendation 3)
 * Keeps multiple active alerts visible while allowing individual dismissals.
 *
 * @param {any} alertData
 * @param {any[]} [allAlerts]
 */
export function renderAnomalyAlert(alertData, allAlerts = null) {
  const banner = document.getElementById('dash-alert-banner');
  if (!banner) return;

  const rawList = Array.isArray(allAlerts) && allAlerts.length > 0
    ? allAlerts
    : Array.isArray(alertData)
      ? alertData
      : alertData?.hasIncident
        ? [alertData]
        : [];

  const dismissedKey = 'puzzle_admin_dismissed_alert';

  // Filter out alerts dismissed in this session
  const activeAlerts = rawList.filter(item => {
    if (!item || !item.hasIncident) return false;
    const sig = `${item.type}:${item.timestamp}:${item.title}`;
    try {
      if (typeof sessionStorage !== 'undefined') {
        if (sessionStorage.getItem(`${dismissedKey}_${sig}`) === '1') return false;
        if (sessionStorage.getItem(dismissedKey) === sig) return false;
      }
    } catch {}
    return true;
  });

  // Clean up any extra dynamic alert banners from previous render
  const parent = banner.parentElement;
  if (parent && typeof parent.querySelectorAll === 'function') {
    const extraBanners = parent.querySelectorAll('.dash-alert-banner-extra');
    for (const b of extraBanners) {
      if (b && typeof b.remove === 'function') b.remove();
    }
  }

  if (activeAlerts.length === 0) {
    banner.style.display = 'none';
    return;
  }

  // Render the primary alert in the fixed DOM banner
  const primaryAlert = activeAlerts[0];
  const primarySig = `${primaryAlert.type}:${primaryAlert.timestamp}:${primaryAlert.title}`;
  const severity = primaryAlert.severity || 'warning';
  banner.className = `dash-alert-banner alert-${severity}`;
  banner.style.display = 'flex';

  const iconEl = document.getElementById('dash-alert-icon');
  if (iconEl) {
    iconEl.innerHTML = severity === 'critical' ? CRITICAL_ICON_SVG : primaryAlert.type === 'latency' ? BOLT_ICON_SVG : ALERT_ICON_SVG;
  }

  const titleEl = document.getElementById('dash-alert-title');
  if (titleEl) titleEl.textContent = primaryAlert.title || 'Incident Alert';

  const msgEl = document.getElementById('dash-alert-message');
  if (msgEl) {
    const rawMsg = String(primaryAlert.message || 'An anomaly has been detected.');
    msgEl.textContent = rawMsg.length > 200 ? rawMsg.slice(0, 197) + '...' : rawMsg;
  }

  const actionBtn = document.getElementById('dash-alert-action-btn');
  if (actionBtn) {
    actionBtn.textContent = `${primaryAlert.actionText || 'Inspect'} →`;
    actionBtn.onclick = (e) => {
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent('dashboard:alert-action', {
        detail: {
          target: primaryAlert.actionTarget,
          errorId: primaryAlert.errorId,
          type: primaryAlert.type
        }
      }));
    };
  }

  const dismissBtn = document.getElementById('dash-alert-dismiss-btn');
  if (dismissBtn) {
    dismissBtn.onclick = (e) => {
      e.stopPropagation();
      try {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(`${dismissedKey}_${primarySig}`, '1');
          sessionStorage.setItem(dismissedKey, primarySig);
        }
      } catch {}
      // Rerender remaining active alerts so they stay visible
      renderAnomalyAlert(null, activeAlerts.slice(1));
    };
  }

  // Render additional active alerts as extra banners so none are hidden
  if (parent && activeAlerts.length > 1 && typeof document.createElement === 'function') {
    let prevNode = banner;
    for (let i = 1; i < activeAlerts.length; i++) {
      const alert = activeAlerts[i];
      const extraBanner = createAlertBannerElement(alert, dismissedKey, () => {
        renderAnomalyAlert(primaryAlert, activeAlerts.filter(a => a !== alert));
      });
      if (extraBanner && typeof parent.insertBefore === 'function') {
        parent.insertBefore(extraBanner, prevNode.nextSibling);
        prevNode = extraBanner;
      }
    }
  }
}

/**
 * Populates all dashboard elements from server response
 * @param {any} data
 */
function populateDashboard(data) {
  if (!data) return;

  // Incident & Anomaly Alert Banner (Recommendation 3)
  renderAnomalyAlert(data.anomalyAlert, data.anomalyAlerts);

  // Key Metric Cards
  const vis = data.visitors || {};
  setEl('dash-visitors-today', vis.uniqueToday);
  setEl('dash-visitors-7d', vis.unique7d);
  setEl('dash-visitors-30d', vis.unique30d);

  const rms = data.rooms || {};
  setEl('dash-online-value', rms.onlinePlayers ?? 0);
  setEl('dash-peak-value', vis.peakConcurrent ?? 0);

  const imgs = data.images || {};
  setEl('dash-images-total', imgs.published ?? '—');
  setEl('dash-images-featured', imgs.featured ?? '—');

  const usr = data.users || {};
  setEl('dash-users-total', usr.total ?? '—');
  setEl('dash-users-discord', usr.discord ?? '—');
  setEl('dash-users-web', usr.web ?? '—');

  setEl('dash-rooms-active', rms.active ?? '—');
  setEl('dash-rooms-alltime', rms.totalAllTime ?? '—');

  setEl('dash-playtime-value', formatPlaytime(data.playtime?.total ?? usr.totalPlaytimeSeconds));

  // System Health
  renderSystemHealth(data.systemHealth, data);

  // Sparklines on Metric Cards
  renderSparkline('dash-visitors-sparkline', data.visitors?.sparkline || data.sparklines?.visitors, {
    color: '#38bdf8',
    gradientId: 'grad-vis'
  });
  renderSparkline('dash-online-sparkline', data.online?.sparkline || data.sparklines?.online, {
    color: '#4ade80',
    gradientId: 'grad-online'
  });
  renderSparkline('dash-images-sparkline', data.images?.sparkline || data.sparklines?.images, {
    color: '#fbbf24',
    gradientId: 'grad-imgs'
  });
  renderSparkline('dash-users-sparkline', data.users?.sparkline || data.sparklines?.users, {
    color: '#a855f7',
    gradientId: 'grad-users'
  });
  renderSparkline('dash-rooms-sparkline', data.rooms?.sparkline || data.sparklines?.rooms, {
    color: '#38bdf8',
    gradientId: 'grad-rooms'
  });
  renderSparkline('dash-playtime-sparkline', data.playtime?.sparkline || data.sparklines?.playtime, {
    color: '#38bdf8',
    gradientId: 'grad-playtime'
  });

  // Operations Row
  const ops = data.operations || {};
  toggleOp('dash-ops-errors', 'dash-ops-errors-count', ops.openErrors);
  toggleOp('dash-ops-flags', 'dash-ops-flags-count', ops.pendingFlags);
  toggleOp('dash-ops-support', 'dash-ops-support-count', ops.openSupport);
  toggleOp('dash-ops-bots', 'dash-ops-bots-count', ops.activeBots);

  // Charts
  const visitorTrend = (vis.trend || []).slice(-10).map((/** @type {any} */ d) => {
    if (d && typeof d.label === 'string' && typeof d.value === 'number') return d;
    const date = d?.day ? new Date(Number(d.day)) : new Date();
    const label = `${date.getMonth() + 1}/${date.getDate()}`;
    return { label, value: Number(d?.count ?? d?.value ?? 0) };
  });
  if (visitorTrend.length > 0) {
    renderBarChart('dash-visitors-chart', visitorTrend, { barColor: '#38bdf8', height: 160, showLabels: true });
  } else {
    setElHtml('dash-visitors-chart', '<div class="dash-chart-empty">No visitor data in the last 10 days</div>');
  }

  // Registration Trend
  const regTrend = (usr.registrationTrend || []).slice(-10).map((/** @type {any} */ d) => {
    const date = d.day ? new Date(Number(d.day)) : new Date();
    const label = `${date.getMonth() + 1}/${date.getDate()}`;
    return { label, value: d.count || 0 };
  });

  if (regTrend.length > 0) {
    renderBarChart('dash-registrations-chart', regTrend, { barColor: '#38bdf8', height: 160, showLabels: true });
  } else {
    setElHtml('dash-registrations-chart', '<div class="dash-chart-empty">No registrations in the last 10 days</div>');
  }

  // Hourly Activity Heatmap (Recommendation 2)
  renderActivityHeatmap('dash-activity-heatmap', data.activityHeatmap);

  // Devices Donut
  const devices = vis.devices || [];
  if (devices.length > 0) {
    renderDonutChart('dash-devices-chart', devices);
  } else {
    setElHtml('dash-devices-chart', '<div class="dash-chart-empty">Device data collecting</div>');
  }

  // Lists
  renderCountriesList('dash-countries-list', vis.topCountries || []);
  renderImagesList('dash-popular-images', imgs.popular || [], false);
  renderImagesList('dash-recent-images', imgs.recent || [], true);
  renderActiveUsersList('dash-active-users-list', usr.activeList || []);
  renderActiveRoomsList('dash-active-rooms-list', rms.activeList || []);

  const viewUsersLink = document.getElementById('dash-view-active-users');
  if (viewUsersLink && !viewUsersLink.dataset.bound) {
    viewUsersLink.dataset.bound = 'true';
    viewUsersLink.addEventListener('click', (e) => {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('dashboard:navigate-active-users'));
    });
  }

  const viewRoomsLink = document.getElementById('dash-view-active-rooms');
  if (viewRoomsLink && !viewRoomsLink.dataset.bound) {
    viewRoomsLink.dataset.bound = 'true';
    viewRoomsLink.addEventListener('click', (e) => {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('dashboard:navigate-active-rooms'));
    });
  }

  // Categories horizontal bars: { name, count } -> { label, value }
  const catData = (imgs.byCategory || []).map((/** @type {any} */ c) => ({
    label: c.name || 'Other',
    value: c.count || 0
  }));
  renderHorizontalBars('dash-categories-list', catData);

  // Puzzle Completion Velocity & Difficulty Breakdown (Improvement 1)
  renderCompletionVelocity('dash-completion-content', data.completionVelocity);

  // Trending Artwork Leaderboard (Improvement 2)
  renderTrendingPuzzles('dash-trending-images', data.trendingPuzzles);

  // Community Milestones Live Feed (Improvement 3)
  renderMilestonesFeed('dash-milestones-stream', data.milestones);

  const viewTrendingLink = document.getElementById('dash-view-trending-images');
  if (viewTrendingLink && !viewTrendingLink.dataset.bound) {
    viewTrendingLink.dataset.bound = 'true';
    viewTrendingLink.addEventListener('click', (e) => {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('dashboard:navigate-catalog'));
    });
  }

  // Tag Cloud
  renderTagCloud('dash-tag-cloud', data.tags || []);
}

/**
 * Format playtime seconds into human readable string using a single unit:
 * "mins", "hrs", "days", "mths", or "yrs".
 * @param {number} totalSeconds
 * @returns {string}
 */
export function formatPlaytime(totalSeconds) {
  if (!totalSeconds || typeof totalSeconds !== 'number' || totalSeconds <= 0) return '—';
  const YEAR = 365 * 86400;
  const MONTH = 30 * 86400;
  const DAY = 86400;
  const HOUR = 3600;

  if (totalSeconds >= YEAR) {
    const val = (totalSeconds / YEAR).toFixed(1).replace(/\.0$/, '');
    return `${val} yrs`;
  }
  if (totalSeconds >= MONTH) {
    const val = (totalSeconds / MONTH).toFixed(1).replace(/\.0$/, '');
    return `${val} mths`;
  }
  if (totalSeconds >= DAY) {
    const val = (totalSeconds / DAY).toFixed(1).replace(/\.0$/, '');
    return `${val} days`;
  }
  if (totalSeconds >= HOUR) {
    const val = (totalSeconds / HOUR).toFixed(1).replace(/\.0$/, '');
    return `${val} hrs`;
  }
  const mins = Math.floor(totalSeconds / 60);
  return `${mins.toLocaleString()} mins`;
}

/**
 * Format epoch time to relative string
 * @param {number} epochMs
 * @returns {string}
 */
function relativeTime(epochMs) {
  if (!epochMs) return '';
  const diff = Date.now() - epochMs;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const d = new Date(epochMs);
  return `${d.toLocaleString('default', { month: 'short' })} ${d.getDate()}`;
}

/**
 * Renders the 7-day × 24-hour activity heatmap matrix (Recommendation 2)
 * @param {string} containerId
 * @param {any} heatmapData
 */
export function renderActivityHeatmap(containerId, heatmapData) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';

  const peakBadge = document.getElementById('dash-heatmap-peak-badge');
  const unit = heatmapData?.unit || 'memberships by latest join';
  if (peakBadge) {
    if (heatmapData?.peak && heatmapData.peak.value > 0) {
      const peakHourStr = `${String(heatmapData.peak.hour).padStart(2, '0')}:00 UTC`;
      peakBadge.innerHTML = `${FLAME_ICON_SVG} Peak: ${heatmapData.peak.day}s @ ${peakHourStr} (${heatmapData.peak.value} ${unit})`;
      if (!peakBadge.textContent) {
        peakBadge.textContent = `Peak: ${heatmapData.peak.day}s @ ${peakHourStr} (${heatmapData.peak.value} ${unit})`;
      }
    } else {
      peakBadge.textContent = 'Activity: Nominal distribution';
    }
  }

  if (!heatmapData || !heatmapData.grid || !Array.isArray(heatmapData.grid) || heatmapData.grid.length === 0) {
    container.innerHTML = '<div class="dash-chart-empty">No activity recorded across the past 7 days</div>';
    return;
  }

  const days = heatmapData.days || ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const grid = heatmapData.grid;
  const maxVal = Math.max(heatmapData.max || 1, 1);

  // Layout parameters for responsive SVG viewBox
  const labelWidth = 38;
  const headerHeight = 22;
  const cols = 24;
  const rows = 7;
  const cellWidth = 19;
  const cellHeight = 15;
  const cellGap = 3;

  const totalWidth = labelWidth + cols * (cellWidth + cellGap) + 10;
  const totalHeight = headerHeight + rows * (cellHeight + cellGap) + 20;

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
  svg.setAttribute('class', 'dash-heatmap-svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');

  // Hour markers on top header (e.g. 00h, 03h, 06h, 09h, 12h, 15h, 18h, 21h)
  for (let h = 0; h < cols; h += 3) {
    const x = labelWidth + h * (cellWidth + cellGap) + cellWidth / 2;
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('x', String(x));
    text.setAttribute('y', String(headerHeight - 6));
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('class', 'dash-heatmap-header-text');
    text.textContent = `${String(h).padStart(2, '0')}h`;
    svg.appendChild(text);
  }

  // Render rows (days) and cells (hours)
  for (let d = 0; d < rows; d++) {
    const y = headerHeight + d * (cellHeight + cellGap);

    // Day label
    const dayLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    dayLabel.setAttribute('x', String(labelWidth - 8));
    dayLabel.setAttribute('y', String(y + cellHeight - 3));
    dayLabel.setAttribute('text-anchor', 'end');
    dayLabel.setAttribute('class', 'dash-heatmap-day-label');
    dayLabel.textContent = days[d] || '';
    svg.appendChild(dayLabel);

    // Hour cells
    const rowValues = grid[d] || [];
    for (let h = 0; h < cols; h++) {
      const x = labelWidth + h * (cellWidth + cellGap);
      const val = Number(rowValues[h]) || 0;
      const ratio = val / maxVal;

      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', String(x));
      rect.setAttribute('y', String(y));
      rect.setAttribute('width', String(cellWidth));
      rect.setAttribute('height', String(cellHeight));
      rect.setAttribute('rx', '3');
      rect.setAttribute('class', 'dash-heatmap-cell');

      // Color mapping from dark slate base to vibrant cyan
      if (val === 0) {
        rect.setAttribute('fill', 'rgba(255, 255, 255, 0.04)');
      } else {
        const opacity = Math.min(1, Math.max(0.18, ratio)).toFixed(2);
        rect.setAttribute('fill', `rgba(56, 189, 248, ${opacity})`);
        if (ratio > 0.75) {
          rect.setAttribute('stroke', 'rgba(255, 255, 255, 0.4)');
          rect.setAttribute('stroke-width', '0.75');
        }
      }

      // Title tooltip
      const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      const hourFmt = `${String(h).padStart(2, '0')}:00 UTC`;
      title.textContent = `${days[d]} ${hourFmt} — ${val} ${unit}`;
      rect.appendChild(title);

      svg.appendChild(rect);
    }
  }

  // Legend at bottom right
  const legendY = totalHeight - 4;
  const legendTextLess = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  legendTextLess.setAttribute('x', String(totalWidth - 110));
  legendTextLess.setAttribute('y', String(legendY));
  legendTextLess.setAttribute('class', 'dash-heatmap-legend-text');
  legendTextLess.textContent = 'Less';
  svg.appendChild(legendTextLess);

  const legendSteps = [0.04, 0.25, 0.5, 0.75, 1.0];
  legendSteps.forEach((op, idx) => {
    const lRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    lRect.setAttribute('x', String(totalWidth - 85 + idx * 11));
    lRect.setAttribute('y', String(legendY - 8));
    lRect.setAttribute('width', '9');
    lRect.setAttribute('height', '9');
    lRect.setAttribute('rx', '2');
    lRect.setAttribute('fill', op === 0.04 ? 'rgba(255, 255, 255, 0.04)' : `rgba(56, 189, 248, ${op})`);
    svg.appendChild(lRect);
  });

  const legendTextMore = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  legendTextMore.setAttribute('x', String(totalWidth - 25));
  legendTextMore.setAttribute('y', String(legendY));
  legendTextMore.setAttribute('class', 'dash-heatmap-legend-text');
  legendTextMore.textContent = 'More';
  svg.appendChild(legendTextMore);

  container.appendChild(svg);
}

/**
 * Renders an SVG bar chart
 * @param {string} containerId
 * @param {Array<{label: string, value: number}>} data
 * @param {{barColor?: string, height?: number, showLabels?: boolean}} options
 */
function renderBarChart(containerId, data, options = {}) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';

  if (!data || data.length === 0) {
    container.innerHTML = '<div class="dash-chart-empty">No data</div>';
    return;
  }

  const barColor = options.barColor || '#38bdf8';
  const height = options.height || 160;
  const showLabels = options.showLabels ?? true;

  const maxVal = Math.max(...data.map(d => d.value), 1);
  const width = Math.max(container.clientWidth || 400, 300);

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', String(height));

  const count = data.length;
  const slotWidth = width / count;
  const barWidth = Math.max(slotWidth * 0.7, 2);
  const showEvery = count > 15 ? 5 : 1;
  const textHeight = showLabels ? 22 : 0;
  const chartHeight = height - textHeight;

  data.forEach((d, i) => {
    const isUnderHalf = (d.value / maxVal) < 0.5;
    const barHeight = Math.max((d.value / maxVal) * (chartHeight - 10), d.value > 0 ? 3 : 0);
    const x = i * slotWidth + (slotWidth - barWidth) / 2;
    const y = chartHeight - barHeight;

    const colGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    colGroup.setAttribute('class', 'dash-bar-column');

    const hitRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    hitRect.setAttribute('x', String(i * slotWidth));
    hitRect.setAttribute('y', '0');
    hitRect.setAttribute('width', String(slotWidth));
    hitRect.setAttribute('height', String(chartHeight));
    hitRect.setAttribute('fill', 'transparent');
    hitRect.setAttribute('class', 'dash-bar-hit');
    colGroup.appendChild(hitRect);

    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('class', 'dash-bar-rect');
    rect.setAttribute('x', String(x));
    rect.setAttribute('y', String(y));
    rect.setAttribute('width', String(barWidth));
    rect.setAttribute('height', String(barHeight));
    rect.setAttribute('fill', barColor);
    rect.setAttribute('rx', '2');
    rect.style.opacity = '0.85';
    rect.style.transition = 'opacity 0.15s';
    colGroup.appendChild(rect);

    const tooltip = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    tooltip.setAttribute('class', 'dash-bar-tooltip');
    tooltip.setAttribute('x', String(x + barWidth / 2));
    tooltip.setAttribute('text-anchor', 'middle');
    tooltip.setAttribute('font-size', '11px');
    tooltip.setAttribute('font-weight', '600');
    tooltip.textContent = String(d.value);
    tooltip.style.opacity = '0';
    tooltip.style.transition = 'opacity 0.15s';
    tooltip.style.pointerEvents = 'none';

    if (isUnderHalf) {
      tooltip.setAttribute('y', String(Math.max(10, y - 4)));
      tooltip.setAttribute('fill', '#ffffff');
      tooltip.dataset.position = 'above';
    } else {
      tooltip.setAttribute('y', String(y + 11));
      tooltip.setAttribute('fill', '#000000');
      tooltip.dataset.position = 'inside';
    }
    colGroup.appendChild(tooltip);

    const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
    title.textContent = `${d.label}: ${d.value}`;
    colGroup.appendChild(title);

    colGroup.addEventListener('mouseenter', () => {
      rect.style.opacity = '1';
      tooltip.style.opacity = '1';
    });
    colGroup.addEventListener('mouseleave', () => {
      rect.style.opacity = '0.85';
      tooltip.style.opacity = '0';
    });

    svg.appendChild(colGroup);

    if (showLabels && i % showEvery === 0) {
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', String(x + barWidth / 2));
      text.setAttribute('y', String(height - 4));
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('font-size', '11px');
      text.setAttribute('fill', '#94a3b8');
      text.textContent = d.label;
      svg.appendChild(text);
    }
  });

  container.appendChild(svg);
}

/**
 * Renders an SVG donut chart with legend
 * @param {string} containerId
 * @param {Array<{label: string, value: number, color?: string}>} data
 */
function renderDonutChart(containerId, data) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';

  const size = 130;
  const center = size / 2;
  const strokeWidth = 16;
  const radius = center - strokeWidth;
  const circumference = 2 * Math.PI * radius;
  const defaultDeviceColors = {
    desktop: '#38bdf8',
    mobile: '#a855f7',
    tablet: '#fbbf24'
  };
  const defaultColors = ['#38bdf8', '#a855f7', '#fbbf24'];

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));

  const total = data.reduce((sum, d) => sum + (d.value || 0), 0);
  let currentOffset = 0;

  data.forEach((d, i) => {
    const val = d.value || 0;
    const percentage = total === 0 ? 0 : val / total;
    const strokeLength = percentage * circumference;
    const key = String(d.label || '').toLowerCase();
    const color = d.color || defaultDeviceColors[key] || defaultColors[i % defaultColors.length];

    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', String(center));
    circle.setAttribute('cy', String(center));
    circle.setAttribute('r', String(radius));
    circle.setAttribute('fill', 'transparent');
    circle.setAttribute('stroke', color);
    circle.setAttribute('stroke-width', String(strokeWidth));
    circle.setAttribute('stroke-dasharray', `${strokeLength} ${Math.max(0, circumference - strokeLength)}`);
    circle.setAttribute('stroke-dashoffset', String(-currentOffset));
    circle.setAttribute('transform', `rotate(-90 ${center} ${center})`);

    const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
    title.textContent = `${d.label}: ${val} (${(percentage * 100).toFixed(1)}%)`;
    circle.appendChild(title);
    svg.appendChild(circle);

    currentOffset += strokeLength;
  });

  container.appendChild(svg);

  const legend = document.createElement('div');
  legend.className = 'dash-donut-legend';

  data.forEach((d, i) => {
    const item = document.createElement('div');
    item.className = 'dash-legend-item';

    const dot = document.createElement('span');
    dot.className = 'dash-legend-dot';
    const key = String(d.label || '').toLowerCase();
    dot.style.backgroundColor = d.color || defaultDeviceColors[key] || defaultColors[i % defaultColors.length];

    const text = document.createElement('span');
    const val = d.value || 0;
    const percentage = total === 0 ? 0 : (val / total * 100).toFixed(0);
    text.textContent = `${d.label} ${percentage}%`;

    item.appendChild(dot);
    item.appendChild(text);
    legend.appendChild(item);
  });

  container.appendChild(legend);
}

/**
 * Renders horizontal category bars using semantic CSS classes
 * @param {string} containerId
 * @param {Array<{label: string, value: number}>} data
 */
function renderHorizontalBars(containerId, data) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';

  if (!data || data.length === 0) {
    container.innerHTML = '<div class="dash-chart-empty">No categories</div>';
    return;
  }

  const maxVal = Math.max(...data.map(d => d.value), 1);

  data.forEach(d => {
    const row = document.createElement('div');
    row.className = 'dash-category-row';
    row.dataset.category = d.label;

    row.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('dashboard:navigate-category', { detail: d.label }));
    });

    const label = document.createElement('span');
    label.className = 'category-name';
    label.textContent = d.label;

    const track = document.createElement('div');
    track.className = 'category-bar-track';

    const fill = document.createElement('div');
    fill.className = 'category-bar-fill';
    fill.style.width = `${((d.value || 0) / maxVal) * 100}%`;
    track.appendChild(fill);

    const count = document.createElement('span');
    count.className = 'category-count';
    count.textContent = String(d.value || 0);

    row.appendChild(label);
    row.appendChild(track);
    row.appendChild(count);
    container.appendChild(row);
  });
}

/**
 * Renders countries list using semantic CSS classes
 * @param {string} containerId
 * @param {Array<any>} data
 */
function renderCountriesList(containerId, data) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';

  if (!data || data.length === 0) {
    container.innerHTML = '<div class="dash-chart-empty">No country data</div>';
    return;
  }

  const maxVal = Math.max(...data.map(d => d.visitors || 0), 1);

  data.forEach(d => {
    const row = document.createElement('div');
    row.className = 'dash-country-row';

    const code = String(d.country || '').toUpperCase();
    const isIso = code.length === 2 && code !== 'XX';

    const flag = document.createElement('span');
    if (isIso) {
      flag.className = `country-flag fi fi-${code.toLowerCase()}`;
    } else {
      flag.className = 'country-flag';
      flag.innerHTML = GLOBE_ICON_SVG;
    }

    const name = document.createElement('span');
    name.className = 'country-name';
    name.textContent = code;

    const bar = document.createElement('div');
    bar.className = 'country-bar';
    const val = d.visitors || 0;
    bar.style.width = `${Math.max((val / maxVal) * 120, 4)}px`;

    const count = document.createElement('span');
    count.className = 'country-count';
    count.textContent = String(val);

    row.appendChild(flag);
    row.appendChild(name);
    row.appendChild(bar);
    row.appendChild(count);
    container.appendChild(row);
  });
}

/**
 * Renders images list with thumbnails
 * @param {string} containerId
 * @param {Array<any>} data
 * @param {boolean} useTime
 */
function renderImagesList(containerId, data, useTime = false) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';

  if (!data || data.length === 0) {
    container.innerHTML = '<div class="dash-chart-empty">No images</div>';
    return;
  }

  data.forEach(item => {
    const row = document.createElement('div');
    row.className = 'dash-image-row';
    row.dataset.imageId = item.id;

    row.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('dashboard:navigate-image', { detail: item.id }));
    });

    const img = document.createElement('img');
    img.src = resolveAdminMediaUrl(item.thumbnail_url);
    img.alt = item.title || 'Puzzle';
    img.loading = 'lazy';

    const title = document.createElement('span');
    title.className = 'dash-image-title';
    title.textContent = item.title || 'Untitled';

    const meta = document.createElement('span');
    meta.className = 'dash-image-meta';

    if (useTime) {
      meta.textContent = relativeTime(item.created_at);
    } else {
      meta.textContent = `★ ${item.popularity || 0}`;
    }

    row.appendChild(img);
    row.appendChild(title);
    row.appendChild(meta);
    container.appendChild(row);
  });
}

/**
 * Renders tag cloud using semantic CSS classes from _dashboard.scss
 * @param {string} containerId
 * @param {Array<{name: string, count: number}>} tags
 */
function renderTagCloud(containerId, tags) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';

  if (!tags || tags.length === 0) {
    container.innerHTML = '<div class="dash-chart-empty">No tags in catalog</div>';
    return;
  }

  // Show top 30 tags
  const visibleTags = tags.slice(0, 30);

  visibleTags.forEach(t => {
    const pill = document.createElement('span');
    pill.className = 'tag-pill';
    pill.dataset.tag = t.name;

    const size = Math.max(0.72, Math.min(1.4, 0.72 + 0.12 * Math.log2(t.count || 1)));
    pill.style.fontSize = `${size.toFixed(2)}rem`;
    pill.textContent = `${t.name} (${t.count})`;

    pill.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('dashboard:navigate-tag', { detail: t.name }));
    });

    container.appendChild(pill);
  });
}

/**
 * Renders currently active users list
 * @param {string} containerId
 * @param {Array<any>} data
 */
export function renderActiveUsersList(containerId, data) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';

  if (!data || data.length === 0) {
    container.innerHTML = '<div class="dash-chart-empty">No players currently online</div>';
    return;
  }

  data.forEach(item => {
    const row = document.createElement('div');
    row.className = 'dash-user-row';
    row.dataset.userId = item.id;

    row.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('dashboard:navigate-user', {
        detail: { id: item.id, username: item.username, displayName: item.display_name }
      }));
    });

    const avatarWrap = document.createElement('div');
    avatarWrap.className = 'dash-user-avatar-wrap';
    if (item.avatar) {
      const img = document.createElement('img');
      img.src = resolveAdminMediaUrl(item.avatar);
      img.alt = item.username || 'User';
      img.className = 'dash-user-avatar';
      img.loading = 'lazy';
      avatarWrap.appendChild(img);
    } else {
      const initial = document.createElement('span');
      initial.className = 'dash-user-avatar';
      initial.textContent = (item.display_name || item.username || '?')[0].toUpperCase();
      avatarWrap.appendChild(initial);
    }

    const info = document.createElement('div');
    info.className = 'dash-user-info';

    const name = document.createElement('span');
    name.className = 'dash-user-name';
    name.textContent = item.display_name || item.username || 'Anonymous';
    info.appendChild(name);

    const meta = document.createElement('span');
    meta.className = 'dash-user-meta';
    meta.textContent = relativeTime(item.last_active_at) || 'Online';

    row.appendChild(avatarWrap);
    row.appendChild(info);
    row.appendChild(meta);
    container.appendChild(row);
  });
}

/**
 * Renders active rooms with players list
 * @param {string} containerId
 * @param {Array<any>} data
 */
export function renderActiveRoomsList(containerId, data) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';

  if (!data || data.length === 0) {
    container.innerHTML = '<div class="dash-chart-empty">No active rooms with players</div>';
    return;
  }

  data.forEach(item => {
    const row = document.createElement('div');
    row.className = 'dash-room-row';
    row.dataset.roomId = item.id;

    row.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('dashboard:navigate-room', { detail: { id: item.id } }));
    });

    if (item.image_url) {
      const img = document.createElement('img');
      img.src = resolveAdminMediaUrl(item.image_url);
      img.alt = item.name || item.id;
      img.loading = 'lazy';
      row.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'dash-room-info';

    const title = document.createElement('span');
    title.className = 'dash-room-title';
    title.textContent = item.name ? `${item.name} (${item.id})` : item.id;

    const sub = document.createElement('div');
    sub.className = 'dash-room-sub';

    const totalPieces = item.total_pieces || 200;
    const piecesLocked = item.pieces_locked || 0;
    const pct = totalPieces > 0 ? Math.round((piecesLocked / totalPieces) * 100) : 0;
    const progressSpan = document.createElement('span');
    progressSpan.textContent = `${piecesLocked}/${totalPieces} pcs (${pct}%)`;
    sub.appendChild(progressSpan);

    if (item.difficulty) {
      const diffSpan = document.createElement('span');
      diffSpan.className = `badge badge-difficulty badge-difficulty-${String(item.difficulty).toLowerCase()}`;
      diffSpan.textContent = item.difficulty;
      sub.appendChild(diffSpan);
    }

    info.appendChild(title);
    info.appendChild(sub);

    const playersBadge = document.createElement('span');
    playersBadge.className = 'dash-room-players';
    const activeCount = Number(item.active_sessions) || 0;
    playersBadge.innerHTML = `${USERS_ICON_SVG} ${activeCount} active`;

    const actions = document.createElement('div');
    actions.className = 'dash-row-actions';

    const spectateBtn = document.createElement('a');
    spectateBtn.className = 'dash-action-btn';
    spectateBtn.href = `/#room-${encodeURIComponent(item.id)}`;
    spectateBtn.setAttribute('href', `/#room-${encodeURIComponent(item.id)}`);
    spectateBtn.target = '_blank';
    spectateBtn.rel = 'noopener noreferrer';
    spectateBtn.title = 'Spectate / Join Room';
    spectateBtn.setAttribute('aria-label', 'Spectate room in new tab');
    spectateBtn.innerHTML = GAMEPAD_ICON_SVG;
    spectateBtn.addEventListener('click', (e) => e.stopPropagation());
    actions.appendChild(spectateBtn);

    const copyBtn = document.createElement('button');
    copyBtn.type = 'button';
    copyBtn.className = 'dash-action-btn';
    copyBtn.title = 'Copy Invite Link';
    copyBtn.setAttribute('aria-label', 'Copy room invite link');
    copyBtn.innerHTML = COPY_ICON_SVG;
    copyBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const roomUrl = `${origin}/#room-${encodeURIComponent(item.id)}`;
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(roomUrl).then(() => {
          copyBtn.innerHTML = CHECK_ICON_SVG;
          copyBtn.classList.add('dash-action-success');
          setTimeout(() => {
            copyBtn.innerHTML = COPY_ICON_SVG;
            copyBtn.classList.remove('dash-action-success');
          }, 1800);
        }).catch(() => {});
      }
    });
    actions.appendChild(copyBtn);

    row.appendChild(info);
    row.appendChild(playersBadge);
    row.appendChild(actions);
    container.appendChild(row);
  });
}

/**
 * Formats a duration in seconds into a human-readable string (e.g., "14m 20s", "1h 15m", "45s").
 * @param {number|null|undefined} seconds
 * @returns {string}
 */
export function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.round(Number(seconds));
  if (!Number.isFinite(s) || s <= 0) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const remS = s % 60;
  if (m < 60) {
    return remS > 0 ? `${m}m ${remS}s` : `${m}m`;
  }
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
}

/**
 * Renders the Puzzle Completion Velocity & Difficulty Breakdown panel (Improvement 1)
 * @param {string} containerId - Container element ID (e.g. 'dash-completion-content')
 * @param {any} data - Velocity data object
 */
export function renderCompletionVelocity(containerId, data) {
  const container = document.getElementById(containerId);
  if (!container) return;

  const rate = Number(data?.rate) || 0;
  const started = Number(data?.totalStarted) || 0;
  const completed = Number(data?.totalCompleted) || 0;
  const avgSeconds = Number(data?.avgSolveSeconds) || 0;

  // Header rate badge
  const rateBadge = document.getElementById('dash-completion-rate-badge');
  if (rateBadge) {
    rateBadge.textContent = `7-Day Rate: ${rate}%`;
    rateBadge.className = `badge badge-difficulty ${rate >= 70 ? 'badge-difficulty-easy' : rate >= 40 ? 'badge-difficulty-normal' : 'badge-difficulty-hard'}`;
  }

  // Summary stat cards
  setEl('dash-velocity-rate', `${rate}%`);
  const barFill = document.getElementById('dash-velocity-bar-fill');
  if (barFill) {
    barFill.style.width = `${Math.min(100, Math.max(0, rate))}%`;
  }

  setEl('dash-velocity-started', started.toLocaleString());
  setEl('dash-velocity-completed', completed.toLocaleString());
  setEl('dash-velocity-avg-time', formatDuration(avgSeconds));

  // Difficulty Grid
  const grid = document.getElementById('dash-difficulty-grid');
  if (!grid) return;
  grid.innerHTML = '';

  const tiers = Array.isArray(data?.tiers) ? data.tiers : [];
  if (tiers.length === 0) {
    grid.innerHTML = '<div class="dash-chart-empty">No difficulty data available</div>';
    return;
  }

  tiers.forEach(tier => {
    const card = document.createElement('div');
    card.className = 'dash-difficulty-card';
    card.dataset.difficulty = tier.id;

    // Header: Badge + Piece range
    const header = document.createElement('div');
    header.className = 'dash-diff-card-header';

    const badge = document.createElement('span');
    badge.className = `badge badge-difficulty badge-difficulty-${String(tier.id).toLowerCase()}`;
    badge.textContent = tier.label || tier.id;
    header.appendChild(badge);

    const range = document.createElement('span');
    range.className = 'dash-diff-range';
    range.textContent = tier.pieceRange || '';
    header.appendChild(range);
    card.appendChild(header);

    // Solve duration chip
    const timeChip = document.createElement('div');
    timeChip.className = 'dash-diff-time-chip';
    const durationStr = formatDuration(tier.avgSeconds);
    timeChip.innerHTML = `${CLOCK_ICON_SVG} ${durationStr}`;
    if (!timeChip.textContent) {
      timeChip.textContent = durationStr;
    }
    timeChip.title = `Average solve time for ${tier.label}: ${durationStr}`;
    card.appendChild(timeChip);

    // Completion stats & mini progress bar
    const statsRow = document.createElement('div');
    statsRow.className = 'dash-diff-stats-row';

    const countSpan = document.createElement('span');
    countSpan.className = 'dash-diff-count';
    countSpan.textContent = `${tier.completed}/${tier.started} solved`;
    statsRow.appendChild(countSpan);

    const rateSpan = document.createElement('span');
    rateSpan.className = 'dash-diff-rate';
    rateSpan.textContent = `${tier.rate}%`;
    statsRow.appendChild(rateSpan);
    card.appendChild(statsRow);

    const track = document.createElement('div');
    track.className = 'dash-diff-bar-track';
    const fill = document.createElement('div');
    fill.className = `dash-diff-bar-fill fill-${String(tier.id).toLowerCase()}`;
    fill.style.width = `${Math.min(100, Math.max(0, tier.rate))}%`;
    track.appendChild(fill);
    card.appendChild(track);

    grid.appendChild(card);
  });
}

/**
 * Renders the Trending Artwork Leaderboard panel (Improvement 2)
 * @param {string} containerId - Element ID for list (e.g. 'dash-trending-images')
 * @param {Array<any>} data - Trending artwork array
 */
export function renderTrendingPuzzles(containerId, data) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';

  if (!data || !Array.isArray(data) || data.length === 0) {
    container.innerHTML = '<div class="dash-chart-empty">No trending puzzles this week</div>';
    return;
  }

  data.forEach((item, index) => {
    const row = document.createElement('div');
    row.className = 'dash-trending-row';
    row.dataset.imageId = item.imageId || '';

    row.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('dashboard:navigate-image', { detail: item.imageId }));
    });

    // Rank badge (#1, #2, etc.)
    const rank = document.createElement('span');
    rank.className = `dash-trending-rank rank-${index + 1}`;
    rank.textContent = `#${index + 1}`;
    row.appendChild(rank);

    // Thumbnail
    const img = document.createElement('img');
    img.src = resolveAdminMediaUrl(item.thumbnailUrl);
    img.alt = item.title || 'Trending Puzzle';
    img.className = 'dash-trending-thumb';
    img.loading = 'lazy';
    row.appendChild(img);

    // Info: Title & Category
    const info = document.createElement('div');
    info.className = 'dash-trending-info';

    const title = document.createElement('span');
    title.className = 'dash-trending-title';
    title.textContent = item.title || 'Untitled Puzzle';
    info.appendChild(title);

    const cat = document.createElement('span');
    cat.className = 'dash-trending-category';
    cat.textContent = item.category || 'Other';
    info.appendChild(cat);
    row.appendChild(info);

    // Metrics: Plays & Solves
    const metrics = document.createElement('div');
    metrics.className = 'dash-trending-metrics';

    const plays = document.createElement('span');
    plays.className = 'dash-trending-metric';
    plays.innerHTML = `${GAMEPAD_ICON_SVG} ${item.playCount} plays`;
    metrics.appendChild(plays);

    row.appendChild(metrics);

    // Quick Inspect/Edit shortcut button
    const inspectBtn = document.createElement('button');
    inspectBtn.type = 'button';
    inspectBtn.className = 'dash-action-btn';
    inspectBtn.title = 'Inspect in Catalog';
    inspectBtn.setAttribute('aria-label', `Inspect ${item.title} in Catalog`);
    inspectBtn.innerHTML = SEARCH_ICON_SVG;
    inspectBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent('dashboard:navigate-image', { detail: item.imageId }));
    });
    row.appendChild(inspectBtn);

    container.appendChild(row);
  });
}

/**
 * Renders the Real-Time Community Milestones Live Feed (Improvement 3)
 * @param {string} containerId - Element ID for list (e.g. 'dash-milestones-stream')
 * @param {Array<any>} data - Milestones events array
 */
export function renderMilestonesFeed(containerId, data) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';

  if (!data || !Array.isArray(data) || data.length === 0) {
    container.innerHTML = '<div class="dash-chart-empty">No recent community milestones</div>';
    return;
  }

  data.forEach(item => {
    const el = document.createElement('div');
    el.className = `dash-milestone-item milestone-${item.type || 'generic'}`;
    el.dataset.milestoneId = item.id || '';

    // If roomId is present, allow clicking to inspect the room
    if (item.roomId) {
      el.classList.add('dash-milestone-clickable');
      el.title = 'Click to inspect room';
      el.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('dashboard:navigate-room', { detail: { id: item.roomId } }));
      });
    } else if (item.target === 'flags') {
      el.classList.add('dash-milestone-clickable');
      el.title = 'Click to review flags';
      el.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('dashboard:alert-action', { detail: { target: 'flags' } }));
      });
    }

    // Icon
    const iconWrap = document.createElement('div');
    iconWrap.className = `dash-milestone-icon-wrap icon-${item.type || 'generic'}`;
    iconWrap.innerHTML = getMilestoneSvgIcon(item.type, item.icon);
    el.appendChild(iconWrap);

    // Body
    const body = document.createElement('div');
    body.className = 'dash-milestone-body';

    const header = document.createElement('div');
    header.className = 'dash-milestone-header';

    const title = document.createElement('span');
    title.className = 'dash-milestone-title';
    title.textContent = item.title || 'Community Event';
    header.appendChild(title);

    const time = document.createElement('span');
    time.className = 'dash-milestone-time';
    time.textContent = relativeTime(item.timestamp);
    if (item.timestamp) {
      time.title = new Date(item.timestamp).toLocaleString();
    }
    header.appendChild(time);
    body.appendChild(header);

    const detail = document.createElement('div');
    detail.className = 'dash-milestone-detail';
    detail.textContent = item.detail || '';
    body.appendChild(detail);

    el.appendChild(body);
    container.appendChild(el);
  });
}


